'use client';

import { Entity } from '../types';
import { DEFAULT_FALLBACK_IMAGE, toOptimizedImageSrc } from './normalizeImageSrc';

const INVALID_IMAGE_VALUES = new Set([
  'api',
  'web',
  'portal',
  'owner-portal',
  'custom',
  'manual',
  'n/a',
  'na',
  'none',
  'null',
  'undefined',
  'image',
  'photo',
]);

const isUsableImage = (src?: string | null): src is string => {
  if (!src) {
    return false;
  }
  const trimmed = src.trim();
  if (!trimmed) {
    return false;
  }

  const lowered = trimmed.toLowerCase();
  if (INVALID_IMAGE_VALUES.has(lowered)) {
    return false;
  }

  // Any real image reference (URL, S3 key, filename) contains a '.' or '/'.
  // Rejects provenance junk like "RIDB feed (US)" stored in display_src by
  // the US feed, which otherwise became a 404 Imgix URL on cards.
  if (!/[./]/.test(trimmed)) {
    return false;
  }

  return true;
};

export interface EntityImageOptions {
  width?: number;
  quality?: number;
  fallback?: string;
}

export const getOptimizedEntityImage = (
  entity: Entity | null | undefined,
  options: EntityImageOptions = {}
): string => {
  if (!entity) {
    return DEFAULT_FALLBACK_IMAGE;
  }

  const { width = 700, quality = 75, fallback = DEFAULT_FALLBACK_IMAGE } = options;
  const { gallery_images, image_src, display_src } = entity;
  let selectedImage: string | undefined;

  if (Array.isArray(gallery_images) && gallery_images.length > 0) {
    const featured = gallery_images.find(
      (img) => img?.is_featured && isUsableImage(img?.image_src)
    );
    if (featured?.image_src) {
      selectedImage = featured.image_src;
    } else {
      const sorted = [...gallery_images]
        .filter((img) => isUsableImage(img?.image_src))
        .sort((a, b) => {
          const orderA =
            typeof a?.sort_order === 'number' ? a.sort_order : Number.MAX_SAFE_INTEGER;
          const orderB =
            typeof b?.sort_order === 'number' ? b.sort_order : Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        });
      if (sorted[0]?.image_src) {
        selectedImage = sorted[0].image_src;
      }
    }
  }

  if (!selectedImage && isUsableImage(image_src)) {
    selectedImage = image_src;
  }

  if (!selectedImage && isUsableImage(display_src)) {
    selectedImage = display_src;
  }

  // fallback '' means "tell me whether it has a photo": answer '' rather
  // than the stock default (EntityCard's hasOwnImage, the planner's photo
  // filter). It used to come back as the default photo, so every listing
  // looked like it had one, and the typed default then 404'd at imgix.
  if (!selectedImage && !fallback) {
    return '';
  }

  const imageToUse = selectedImage ?? fallback;

  return toOptimizedImageSrc(imageToUse, { width, quality });
};

/**
 * Return an array of optimized image URLs for an entity.
 * Uses gallery_images when available, falling back to image_src / display_src.
 * Featured images are placed first, then sorted by sort_order.
 */
export const getEntityImages = (
  entity: Entity | null | undefined,
  options: { mainWidth?: number; thumbWidth?: number; quality?: number; fallback?: string } = {}
): string[] => {
  if (!entity) return [];

  const {
    mainWidth = 480,
    thumbWidth = 240,
    quality = 75,
    fallback = DEFAULT_FALLBACK_IMAGE,
  } = options;

  const { gallery_images, image_src, display_src } = entity;

  // Collect usable gallery images, featured first then by sort_order
  const usable =
    Array.isArray(gallery_images) && gallery_images.length > 0
      ? [...gallery_images]
          .filter((img) => isUsableImage(img?.image_src))
          .sort((a, b) => {
            if (a.is_featured && !b.is_featured) return -1;
            if (!a.is_featured && b.is_featured) return 1;
            const orderA =
              typeof a?.sort_order === 'number' ? a.sort_order : Number.MAX_SAFE_INTEGER;
            const orderB =
              typeof b?.sort_order === 'number' ? b.sort_order : Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
          })
          .map((img) => img.image_src)
      : [];

  // If no gallery images, fall back to entity-level fields
  if (usable.length === 0) {
    if (isUsableImage(image_src)) usable.push(image_src);
    else if (isUsableImage(display_src)) usable.push(display_src);
    else usable.push(fallback);
  }

  // Optimise: first image at main width, rest at thumbnail width
  return usable.map((src, i) =>
    toOptimizedImageSrc(src, { width: i === 0 ? mainWidth : thumbWidth, quality })
  );
};
